import {
  Box,
  Stack,
  Avatar,
  Collapse,
  MenuItem,
  MenuList,
  ClickAwayListener,
  Popover,
  Menu,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { LogOutIcon, ProfileIcon } from './icons'
import { useCompany } from '@/hooks/useCompany'
import { Input } from '@/components'
import { FormProvider, useForm } from 'react-hook-form'

export const UserInfo = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleLogOut = () => {
    handleClose()
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    window.location.reload()
  }

  function stringToColor(string: string) {
    let hash = 0
    let i
    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash)
    }
    let color = '#'
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff
      color += `00${value.toString(16)}`.slice(-2)
    }
    /* eslint-enable no-bitwise */
    return color
  }
  function stringAvatar(name: string) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
    }
  }
  function stringCompanyAvatar(name: string) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: `${name[0]}`,
    }
  }

  const { user, userCompanies, activeCompany, changeCompany } = useCompany()

  const filterForm = useForm()
  const watchFilterForm = filterForm.watch('company_filter')
  const [filterResult, setFilterResult] = useState([...userCompanies])

  useMemo(() => {
    if (watchFilterForm) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const innFilter = userCompanies.filter((e: any) =>
        e.inn.includes(watchFilterForm)
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const titleFilter = userCompanies.filter((e: any) =>
        String(e.title)
          .toLowerCase()
          .includes(String(watchFilterForm).toLowerCase())
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const shortTitleFilter = userCompanies.filter((e: any) =>
        String(e.short_title)
          .toLowerCase()
          .includes(String(watchFilterForm).toLowerCase())
      )
      const result = [...innFilter, ...titleFilter, ...shortTitleFilter].filter(
        (company, index, self) => {
          return index === self.findIndex((item) => item.id === company.id)
        }
      )
      setFilterResult(result)
    } else {
      setFilterResult([...userCompanies])
    }
  }, [watchFilterForm])

  return (
    <Box>
      <Stack
        onClick={handleOpen}
        sx={{
          position: 'relative',
          padding: '12px',
          borderRadius: '4px',
          cursor: 'pointer',
          // boxShadow: open || shadow ? '0px 0px 5px rgba(0,0,0,0.3)' : '',
          '&:hover': {
            // boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
            backgroundColor: '#EEEEEE',
          },
          backgroundColor: anchorEl !== null ? '#EEEEEE' : 'none',
          transition: 'box-shadow 0.1s ease-out',
        }}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'12px'}>
          <Avatar />
          <Stack>
            <Box sx={{ fontSize: '14px', fontWeight: 500, color: '#3E3E3E' }}>
              {user?.lastname + ' ' + user?.firstname + ' ' + user?.middlename}
            </Box>
            <Box sx={{ fontSize: '12px', fontWeight: 400, color: '#8E8E8E' }}>
              {activeCompany?.short_title}
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Popover
        sx={{
          '& .MuiPaper-root': {
            marginTop: '17px',
            boxShadow: '0px 7px 7px 7px rgba(0,0,0,0.05)',
            borderRadius: '6px',
          },
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack>
          <Stack sx={{ flexDirection: 'row' }}>
            <Stack
              sx={{ padding: '12px' }}
              direction={'row'}
              alignItems={'center'}
              gap={'12px'}
            >
              <Avatar />
              <Stack>
                <Box
                  sx={{ fontSize: '16px', fontWeight: 500, color: '#3E3E3E' }}
                >
                  {user?.lastname +
                    ' ' +
                    user?.firstname +
                    ' ' +
                    user?.middlename}
                </Box>
                <Box
                  sx={{ fontSize: '14px', fontWeight: 400, color: '#8E8E8E' }}
                >
                  {activeCompany?.short_title}
                </Box>
              </Stack>
            </Stack>
            <Stack
              sx={{
                padding: '8px 12px',
                '& .MuiButtonBase-root': {
                  borderRadius: '4px',
                  padding: '8px',
                },
                '& .MuiTypography-root': {
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#8E8E8E',
                },
              }}
            >
              <MenuItem>
                <Stack direction={'row'} gap={'12px'}>
                  <ProfileIcon />
                  <Typography>Мой Профиль</Typography>
                </Stack>
              </MenuItem>
              <MenuItem onClick={handleLogOut}>
                <Stack direction={'row'} gap={'12px'}>
                  <LogOutIcon />
                  <Typography>Выйти</Typography>
                </Stack>
              </MenuItem>
            </Stack>
          </Stack>
          <FormProvider {...filterForm}>
            <Box sx={{ padding: '12px' }}>
              <Input
                fullWidth
                InputProps={{
                  autoComplete: 'off',
                  type: 'search',
                }}
                placeholder="Введите компанию"
                name="company_filter"
                sx={{
                  input: {
                    paddingTop: '7px',
                    '&.MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'black',
                    },
                  },
                }}
              />
            </Box>
          </FormProvider>
          <Stack
            sx={{
              padding: '12px',
              backgroundColor: '#EEEEEE',
              borderRadius: '6px',
              gap: '8px',
              '& .MuiButtonBase-root': {
                borderRadius: '4px',
                padding: '8px',
                backgroundColor: '#FFF',
                fontSize: '12px',
                '&:hover': {
                  backgroundColor: '#D4E3F0',
                },
              },
              maxHeight: '360px',
              overflow: 'auto',
            }}
          >
            {/*eslint-disable-next-line @typescript-eslint/no-explicit-any*/}
            {filterResult?.map((item: any) =>
              item?.status === 'DELETED' ? null : (
                <MenuItem
                  key={item.id}
                  onClick={() => {
                    changeCompany(item.id)
                    handleClose()
                  }}
                  sx={{
                    '&.MuiButtonBase-root': {
                      backgroundColor:
                        activeCompany?.id === item.id ? '#009FDA' : 'white',
                      '&:hover': {
                        backgroundColor:
                          activeCompany?.id === item.id ? '#009FDA' : '',
                      },
                    },
                  }}
                >
                  <Stack direction={'row'} alignItems={'center'} gap={'8px'}>
                    <Avatar {...stringCompanyAvatar(item.short_title ?? '')} />
                    <Stack>
                      <Box
                        sx={{
                          fontWeight: 400,
                          color:
                            activeCompany?.id === item.id ? 'white' : '#8E8E8E',
                        }}
                      >
                        Компания
                      </Box>
                      <Box
                        sx={{
                          fontWeight: 500,
                          color: '#3E3E3E',
                        }}
                      >
                        {item?.short_title}
                      </Box>
                    </Stack>
                  </Stack>
                </MenuItem>
              )
            )}
          </Stack>
        </Stack>
      </Popover>
    </Box>
  )
}
